import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { useDeleteReservMutation } from "../../redux/services/rooms/reservationAPI";
import Swal from "sweetalert2";
import { useUpdateOrderMutation, useDeleteOrderMutation } from "../../redux/services/foods/orderAPI";
import { useGetCartQuery } from "../../redux/services/userPnael/userPnaelAPI";
import { BiSolidTrashAlt } from "react-icons/bi";
import { BiSolidCalendarEdit } from "react-icons/bi";
import { UserContext } from "../../components/UserContext";

function Cart() {
  const { user } = useContext(UserContext);
  const { data: cart, isLoading, isError, error, refetch: Cartrefetch } = useGetCartQuery(user?.id);

  const [deletedReservationId, setDeletedReservationId] = useState(null);
  const [deleteReserv] = useDeleteReservMutation();
  const [deleteOrder] = useDeleteOrderMutation();
  const [updateOrder] = useUpdateOrderMutation();

  const reservations = cart?.reservations || [];
  const orders = cart?.orders || [];
  const totalOrderCost = orders.reduce((sum, order) => sum + (order.total_price || Number(order.food?.price || 0) * Number(order.foods_number || 0)), 0);
  const totalReservCost = reservations.reduce((sum, reserv) => sum + Number(reserv.total_price || 0), 0);
  const total = totalOrderCost + totalReservCost;
  const taxes = total * 0.05;

  const DateDifference = (start_date, end_date) => {
    const startDate = new Date(start_date);
    const endDate = new Date(end_date);
    const diffInMilliseconds = Math.abs(endDate - startDate);
    return Math.max(1, Math.ceil(diffInMilliseconds / (1000 * 60 * 60 * 24)));
  };

  const DateTimeFormat = (originalDateString) => {
    return new Date(originalDateString).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDelete = async (id, action) => {
    if (action === "reservation") {
      await deleteReserv(id);
      setDeletedReservationId(id);
    } else {
      await deleteOrder(id);
    }
    await Cartrefetch();
    Swal.fire({ icon: "success", text: "Successfully deleted", timer: 1400, showConfirmButton: false, toast: true, position: "top-end", customClass: { popup: 'sweetalert2-toast-custom' } });
  };

  const changeNumber = async (status, id, foodsNumber) => {
    const nextNumber = status ? foodsNumber + 1 : foodsNumber - 1;
    if (!status && nextNumber < 1) return;

    try {
      await updateOrder({ id, foods_number: nextNumber, Client: user?.id });
      await Cartrefetch();
    } catch {
      Swal.fire({ icon: "warning", text: "Unable to update quantity.", confirmButtonText: 'OK', customClass: { popup: 'sweetalert2-custom-popup' } });
    }
  };

  if (isLoading) {
    return <div className="section-shell py-24 text-center text-muted">Loading cart...</div>;
  }

  if (isError) {
    return <div className="section-shell py-24 text-center text-red-500">Error: {error?.status || error?.message}</div>;
  }

  if (!reservations.length && !orders.length) {
    return (
      <section className="bg-surface py-20">
        <div className="section-shell">
          <div className="premium-card p-10 text-center">
            <h1 className="section-title">Your cart is empty</h1>
            <p className="section-copy mx-auto">Add a room reservation or restaurant order to start building your trip.</p>
            <div className="flex justify-center gap-3">
              <Link to="/rooms" className="btn-primary">Browse rooms</Link>
              <Link to="/foods" className="btn-secondary">Explore dining</Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-surface py-12 sm:py-16">
      <div className="section-shell">
        <div className="mb-8">
          <span className="eyebrow">Checkout cart</span>
          <h1 className="section-title mt-5">Review your trip</h1>
          <p className="section-copy">A single place to manage hotel stays, dining orders, quantities, and totals.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
          <div className="space-y-6">
            {reservations.length > 0 && (
              <div className="table-shell">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-muted">
                      <tr>
                        <th className="px-5 py-4">Room</th>
                        <th className="px-5 py-4">Dates</th>
                        <th className="px-5 py-4">Guests</th>
                        <th className="px-5 py-4">Nights</th>
                        <th className="px-5 py-4">Total</th>
                        <th className="px-5 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {reservations.map((reservation) => (
                        <tr key={reservation.id} className={deletedReservationId === reservation.id ? 'opacity-0 transition duration-300' : ''}>
                          <td className="px-5 py-5">
                            <div className="flex items-center gap-3">
                              <img className="h-14 w-14 rounded-2xl object-cover" src={reservation.room.main_img} alt={reservation.room.room_name} />
                              <div>
                                <p className="font-black text-ink">{reservation.room.room_name}</p>
                                <p className="text-sm text-muted">${reservation.room.price}/night</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-5 text-muted">
                            <p>{reservation.entry_date}</p>
                            <p>{reservation.exit_date}</p>
                          </td>
                          <td className="px-5 py-5 font-bold text-ink">{reservation.persons_number}</td>
                          <td className="px-5 py-5 font-bold text-ink">{DateDifference(reservation.entry_date, reservation.exit_date)}</td>
                          <td className="px-5 py-5 font-black text-ink">${DateDifference(reservation.entry_date, reservation.exit_date) * reservation.room.price}</td>
                          <td className="px-5 py-5">
                            <div className="flex justify-end gap-2">
                              <Link state={{ data: reservation.room, reservation }} to={`/room/${encodeURIComponent(reservation.room.room_name)}`} className="grid h-10 w-10 place-items-center rounded-full bg-gotrip-50 text-gotrip-700 transition hover:bg-gotrip-100" aria-label="Edit reservation">
                                <BiSolidCalendarEdit />
                              </Link>
                              <button type="button" onClick={() => handleDelete(reservation.id, "reservation")} className="grid h-10 w-10 place-items-center rounded-full bg-red-50 text-red-600 transition hover:bg-red-100" aria-label="Delete reservation">
                                <BiSolidTrashAlt />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {orders.length > 0 && (
              <div className="table-shell">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-muted">
                      <tr>
                        <th className="px-5 py-4">Dish</th>
                        <th className="px-5 py-4">Price</th>
                        <th className="px-5 py-4">Quantity</th>
                        <th className="px-5 py-4">Ordered</th>
                        <th className="px-5 py-4">Total</th>
                        <th className="px-5 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {orders.map((order) => (
                        <tr key={order.id}>
                          <td className="px-5 py-5">
                            <div className="flex items-center gap-3">
                              <img className="h-14 w-14 rounded-2xl object-cover" src={order.food.food_img} alt={order.food.name} />
                              <p className="font-black text-ink">{order.food.name}</p>
                            </div>
                          </td>
                          <td className="px-5 py-5 text-muted">${order.food.price}</td>
                          <td className="px-5 py-5">
                            <div className="inline-flex items-center rounded-2xl bg-slate-100 p-1">
                              <button type="button" onClick={() => changeNumber(false, order.id, order.foods_number)} className="grid h-8 w-8 place-items-center rounded-xl font-black text-ink transition hover:bg-white">−</button>
                              <span className="w-8 text-center text-sm font-black">{order.foods_number}</span>
                              <button type="button" onClick={() => changeNumber(true, order.id, order.foods_number)} className="grid h-8 w-8 place-items-center rounded-xl font-black text-ink transition hover:bg-white">+</button>
                            </div>
                          </td>
                          <td className="px-5 py-5 text-muted">{DateTimeFormat(order.order_date)}</td>
                          <td className="px-5 py-5 font-black text-ink">${order.food.price * order.foods_number}</td>
                          <td className="px-5 py-5 text-right">
                            <button type="button" onClick={() => handleDelete(order.id, "order")} className="grid h-10 w-10 place-items-center rounded-full bg-red-50 text-red-600 transition hover:bg-red-100" aria-label="Delete order">
                              <BiSolidTrashAlt />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <aside className="h-fit premium-card bg-white p-6">
            <h2 className="text-xl font-black tracking-tight text-ink">Order summary</h2>
            <div className="mt-6 space-y-4 text-sm">
              <div className="flex justify-between text-muted"><span>Subtotal</span><span>${total}</span></div>
              <div className="flex justify-between text-muted"><span>Service tax</span><span>${taxes}</span></div>
              <div className="border-t border-line pt-4 text-lg font-black text-ink"><div className="flex justify-between"><span>Total</span><span>${total + taxes}</span></div></div>
            </div>
            <Link to="/checkout" className="btn-primary mt-6 w-full">Continue checkout</Link>
            <p className="mt-4 text-xs leading-6 text-muted">Review your selections before completing the reservation.</p>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default Cart;
